import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { FileText, FolderOpen, Users, Eye, Image, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseedingImages, setReseedingImages] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, articlesRes] = await Promise.all([
        axios.get(`${API_URL}/api/stats`, getAuthHeaders()),
        axios.get(`${API_URL}/api/articles?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentArticles(articlesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleReseedImages = async () => {
    if (!window.confirm('This will reassign unique images to all 79 articles. Continue?')) {
      return;
    }
    
    setReseedingImages(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/reseed-images`, {}, getAuthHeaders());
      toast.success(response.data.message);
      // Refresh data to show new images
      fetchData();
    } catch (error) {
      console.error('Error reseeding images:', error);
      toast.error('Failed to reseed images');
    }
    setReseedingImages(false);
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white border border-[#E2E8F0] p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="font-label text-xs text-[#767C91] tracking-widest">{label}</p>
          <p className="font-heading text-2xl text-[#2C2C2C]">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-[#2C2C2C]" data-testid="dashboard-title">Dashboard</h1>
          <p className="font-body text-[#767C91]">Welcome to the LHSC Blog Admin</p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 loading-skeleton"></div>
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard 
                icon={FileText} 
                label="TOTAL ARTICLES" 
                value={stats?.total_articles || 0} 
                color="bg-[#9D6E6D]"
              />
              <StatCard 
                icon={Eye} 
                label="PUBLISHED" 
                value={stats?.published_articles || 0} 
                color="bg-[#ABBDDF]"
              />
              <StatCard 
                icon={FolderOpen} 
                label="CATEGORIES" 
                value={stats?.total_categories || 0} 
                color="bg-[#DBC8CE]"
              />
              <StatCard 
                icon={Users} 
                label="SUBSCRIBERS" 
                value={stats?.total_subscribers || 0} 
                color="bg-[#767C91]"
              />
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Link 
                to="/admin/articles/new"
                className="bg-[#F2E1E1] p-6 hover:shadow-lg transition-shadow"
                data-testid="quick-add-article"
              >
                <h3 className="font-heading text-xl text-[#2C2C2C] mb-2">Add New Article</h3>
                <p className="font-body text-[#767C91] text-sm">Create and publish a new blog article</p>
              </Link>
              <Link 
                to="/admin/categories"
                className="bg-[#ABBDDF] p-6 hover:shadow-lg transition-shadow"
                data-testid="quick-manage-categories"
              >
                <h3 className="font-heading text-xl text-[#2C2C2C] mb-2">Manage Categories</h3>
                <p className="font-body text-[#2C2C2C]/70 text-sm">Add, edit, or remove blog categories</p>
              </Link>
              <button 
                onClick={handleReseedImages}
                disabled={reseedingImages}
                className="bg-[#DBC8CE] p-6 hover:shadow-lg transition-shadow text-left disabled:opacity-50"
                data-testid="reseed-images-btn"
              >
                <div className="flex items-center gap-2 mb-2">
                  {reseedingImages ? (
                    <RefreshCw className="w-5 h-5 text-[#2C2C2C] animate-spin" />
                  ) : (
                    <Image className="w-5 h-5 text-[#2C2C2C]" />
                  )}
                  <h3 className="font-heading text-xl text-[#2C2C2C]">
                    {reseedingImages ? 'Reseeding...' : 'Fix Article Images'}
                  </h3>
                </div>
                <p className="font-body text-[#2C2C2C]/70 text-sm">Reassign unique images to all 79 articles</p>
              </button>
            </div>

            {/* Recent Articles */}
            <div className="bg-white border border-[#E2E8F0] p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl text-[#2C2C2C]">Recent Articles</h2>
                <Link to="/admin/articles" className="font-label text-xs text-[#9D6E6D] tracking-widest hover:text-[#2C2C2C]">
                  View All →
                </Link>
              </div>
              <div className="space-y-4">
                {recentArticles.map((article) => (
                  <div key={article.id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0] last:border-0">
                    <div className="flex items-center gap-4">
                      <img 
                        src={article.hero_image} 
                        alt="" 
                        className="w-12 h-12 object-cover"
                      />
                      <div>
                        <h3 className="font-heading text-sm text-[#2C2C2C] line-clamp-1">{article.title}</h3>
                        <p className="font-label text-xs text-[#767C91]">{article.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 text-xs font-label ${article.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {article.is_published ? 'Published' : 'Draft'}
                      </span>
                      <Link 
                        to={`/admin/articles/edit/${article.id}`}
                        className="font-label text-xs text-[#9D6E6D] hover:text-[#2C2C2C]"
                      >
                        Edit
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
